import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SectionTitle } from '../../components/ui/PageLayout';
import { Waves, FolderKanban, GitFork, ArrowRight, ChevronDown, ChevronRight } from 'lucide-react';

export default function MemberStreamsProjectsTreeSection({
  streams,
  projects,
  repositories,
  unassignedRepositories,
  streamsExpanded,
  projectsExpanded,
  toggleStream,
  toggleProject,
}: any) {
  const navigate = useNavigate();

  return (
    <>
      <div className="mb-2 flex-shrink-0">
        <SectionTitle>Streams · Projects · Repositories</SectionTitle>
      </div>
      <div className="min-h-0 flex-1 flex flex-col">
        {!streams?.length && !projects?.length && !(unassignedRepositories?.length > 0) ? (
          <p className="text-sm text-slate-400">
            Not a member of any stream or project yet. Link a GitHub contributor to show streams,
            projects, and repos where they have recorded activity.
          </p>
        ) : (
          <>
            {(streams?.length > 0 || projects?.length > 0) && (
              <div className="card-mdl-static overflow-hidden rounded-xl border border-dark-border">
                {(() => {
                  const streamIds = [
                    ...new Set(
                      [
                        ...(streams || []).map((s) => s.id),
                        ...(projects || []).map((p) => p.streamId),
                      ].filter(Boolean)
                    ),
                  ];
                  const tree = streamIds.map((sid) => {
                    const stream = (streams || []).find((s) => s.id === sid) || {};
                    const streamName =
                      stream.name ||
                      (projects || []).find((p) => p.streamId === sid)?.streamName ||
                      'Stream';
                    const streamProjects = (projects || []).filter((p) => p.streamId === sid);
                    return {
                      id: sid,
                      name: streamName,
                      slug: stream.slug,
                      projects: streamProjects.map((p) => ({
                        ...p,
                        repos: (repositories || []).filter((r) => r.projectId === p.id),
                      })),
                    };
                  });
                  return tree.map((stream) => {
                    const streamOpen = streamsExpanded.has(stream.id);
                    return (
                      <div key={stream.id} className="border-b border-dark-border last:border-b-0">
                        <div className="flex items-center gap-3 px-4 py-3 group">
                          <button
                            type="button"
                            onClick={() => toggleStream(stream.id)}
                            className="flex items-center gap-3 flex-1 min-w-0 text-left font-medium text-slate-100 hover:bg-dark-elevated/50 transition-colors rounded-lg -m-1 p-1"
                            aria-expanded={streamOpen}
                          >
                            <span className="flex-shrink-0 w-6 flex items-center justify-center text-slate-400">
                              {streamOpen ? (
                                <ChevronDown className="w-4 h-4" />
                              ) : (
                                <ChevronRight className="w-4 h-4" />
                              )}
                            </span>
                            <Waves className="w-5 h-5 text-vivid-cyan-blue flex-shrink-0" />
                            <span className="truncate flex-1">{stream.name}</span>
                            <span className="text-xs text-slate-400 flex-shrink-0">
                              {stream.projects.length} project
                              {stream.projects.length !== 1 ? 's' : ''}
                            </span>
                          </button>
                          <button
                            type="button"
                            onClick={() => navigate(`/streams/${stream.id}`)}
                            className="flex-shrink-0 p-2 rounded-lg text-slate-500 hover:text-vivid-red hover:bg-dark-elevated/50 transition-colors"
                            aria-label={`Open ${stream.name}`}
                          >
                            <ArrowRight className="w-4 h-4" />
                          </button>
                        </div>
                        {streamOpen && (
                          <div className="bg-dark-base/50/30">
                            {stream.projects.length === 0 ? (
                              <p className="px-4 py-3 pl-14 text-sm text-slate-400">
                                No projects in this stream
                              </p>
                            ) : (
                              stream.projects.map((project) => {
                                const projectOpen = projectsExpanded.has(project.id);
                                return (
                                  <div
                                    key={project.id}
                                    className="border-t border-dark-border/80/80 first:border-t-0"
                                  >
                                    <div className="flex items-center gap-3 pl-14 pr-4 py-2.5">
                                      <button
                                        type="button"
                                        onClick={() => toggleProject(project.id)}
                                        className="flex items-center gap-3 flex-1 min-w-0 text-left text-sm text-slate-200 hover:bg-dark-elevated/80/30 transition-colors rounded-lg -m-1 p-1"
                                        aria-expanded={projectOpen}
                                      >
                                        <span className="flex-shrink-0 w-5 flex items-center justify-center text-slate-400">
                                          {projectOpen ? (
                                            <ChevronDown className="w-3.5 h-3.5" />
                                          ) : (
                                            <ChevronRight className="w-3.5 h-3.5" />
                                          )}
                                        </span>
                                        <FolderKanban className="w-4 h-4 text-vivid-purple flex-shrink-0" />
                                        <span className="truncate flex-1">{project.name}</span>
                                        <span className="text-xs text-slate-400 flex-shrink-0">
                                          {project.repos.length} repo
                                          {project.repos.length !== 1 ? 's' : ''}
                                        </span>
                                      </button>
                                      <button
                                        type="button"
                                        onClick={() =>
                                          navigate(
                                            `/streams/${project.streamId}/projects/${project.id}`
                                          )
                                        }
                                        className="flex-shrink-0 p-1.5 rounded-lg text-slate-400 hover:text-vivid-red hover:bg-slate-400/30 transition-colors"
                                        aria-label={`Open ${project.name}`}
                                      >
                                        <ArrowRight className="w-3.5 h-3.5" />
                                      </button>
                                    </div>
                                    {projectOpen && (
                                      <div className="border-t border-dark-border/80/80">
                                        {project.repos.length === 0 ? (
                                          <p className="pl-20 pr-4 py-2 text-sm text-slate-400">
                                            No repositories
                                          </p>
                                        ) : (
                                          project.repos.map((repo) => (
                                            <button
                                              key={repo.id}
                                              type="button"
                                              onClick={() => navigate(`/repositories/${repo.id}`)}
                                              className="w-full flex items-center gap-2 pl-20 pr-4 py-2 text-left text-sm text-slate-400 hover:bg-dark-elevated/80/30 hover:text-vivid-red transition-colors"
                                            >
                                              <GitFork className="w-3.5 h-3.5 text-vivid-red flex-shrink-0 opacity-80" />
                                              <span className="truncate flex-1">
                                                {repo.fullName || repo.name}
                                              </span>
                                              <ArrowRight className="w-3.5 h-3.5 flex-shrink-0 opacity-60" />
                                            </button>
                                          ))
                                        )}
                                      </div>
                                    )}
                                  </div>
                                );
                              })
                            )}
                          </div>
                        )}
                      </div>
                    );
                  });
                })()}
              </div>
            )}
            {unassignedRepositories?.length > 0 && (
              <div className="mt-4">
                <p className="text-sm text-slate-400 mb-2">
                  Also contributing to organisation repositories not yet assigned to a project:
                </p>
                <ul className="card-mdl-static divide-y divide-dark-border rounded-xl border border-dark-border overflow-hidden">
                  {unassignedRepositories.map((repo) => (
                    <li key={repo.id}>
                      <button
                        type="button"
                        onClick={() => navigate(`/repositories/${repo.id}`)}
                        className="w-full flex items-center gap-2 px-4 py-3 text-left text-sm text-slate-200 hover:bg-dark-base/50"
                      >
                        <GitFork className="w-4 h-4 text-vivid-red flex-shrink-0" />
                        <span className="truncate flex-1">{repo.fullName || repo.name}</span>
                        <ArrowRight className="w-4 h-4 flex-shrink-0 text-slate-400" />
                      </button>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}
